// ===============================================
// 👑 TELA ADMINISTRATIVA COM SIDEBAR
// ===============================================
// Arquivo: src/pages/AdminPage.js

import React, { useState, useCallback, useTransition, lazy, Suspense, useEffect } from 'react';
import AdminSidebar from '../components/admin/AdminSidebar';
import './AdminPage.css';

// Lazy-load heavy page components to avoid blocking UI on menu switch
const loadUsuariosSection = () => import('../components/admin/UsuariosSection');
const loadRelatoriosSection = () => import('../components/admin/RelatoriosSection');
const loadRelatorioExpedicao = () => import('../components/admin/RelatorioExpedicao');
const loadRelatoriosHome = () => import('../components/admin/RelatoriosHome');
const loadConfiguracoesHome = () => import('../components/admin/ConfiguracoesHome');
const loadGestaoExpeditionPage = () => import('./GestaoExpeditionPage');

const UsuariosSection = lazy(loadUsuariosSection);
const RelatoriosSection = lazy(loadRelatoriosSection);
const RelatorioExpedicao = lazy(loadRelatorioExpedicao);
const RelatoriosHome = lazy(loadRelatoriosHome);
const ConfiguracoesHome = lazy(loadConfiguracoesHome);
const GestaoExpeditionPage = lazy(loadGestaoExpeditionPage);

const SuspenseFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 200 }}>
    <div style={{ textAlign: 'center', color: '#888' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
      <p>Carregando...</p>
    </div>
  </div>
);

const SIDEBAR_COLLAPSED_KEY = 'admin_sidebar_collapsed';

const AdminPage = () => {
  const [activeMenu, setActiveMenu] = useState('gestao');
  const [activeContent, setActiveContent] = useState('gestao');
  const [relatorioSelecionado, setRelatorioSelecionado] = useState(null);
  const [configuracaoSelecionada, setConfiguracaoSelecionada] = useState(null);
  const [isPending, startTransition] = useTransition();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  });

  const handleToggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      } catch (e) {
        // ignore storage errors (private mode, etc.)
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const preloadSections = () => {
      loadRelatoriosHome();
      loadConfiguracoesHome();
      loadRelatoriosSection();
      loadRelatorioExpedicao();
      loadUsuariosSection();
      loadGestaoExpeditionPage();
    };

    // Preload idle chunks to avoid parsing/loading cost in click handlers.
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(preloadSections, { timeout: 1200 });
      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = window.setTimeout(preloadSections, 300);
    return () => window.clearTimeout(timeoutId);
  }, []);

  // ===============================================
  // 🔄 HANDLERS DE NAVEGAÇÃO
  // ===============================================
  const handleMenuChange = useCallback((menu) => {
    if (menu === activeMenu) return;

    // Update sidebar highlight urgently so the click feels instant.
    setActiveMenu(menu);

    // Defer the heavy content swap so React can yield mid-render.
    startTransition(() => {
      setActiveContent(menu);
      if (menu !== 'relatorios') {
        setRelatorioSelecionado(null);
      }
      if (menu !== 'configuracoes') {
        setConfiguracaoSelecionada(null);
      }
    });
  }, [activeMenu, startTransition]);

  const handleSelectConfiguracao = (tipoConfiguracao) => {
    setConfiguracaoSelecionada(tipoConfiguracao);
  };

  const handleVoltarConfiguracoes = () => {
    setConfiguracaoSelecionada(null);
  };

  const handleSelectRelatorio = (tipoRelatorio) => {
    setRelatorioSelecionado(tipoRelatorio);
  };

  const handleVoltarRelatorios = () => {
    setRelatorioSelecionado(null);
  };

  // ===============================================
  // 🎨 RENDERIZAÇÃO DO CONTEÚDO BASEADO NO MENU
  // ===============================================
  const renderContent = () => {
    switch (activeContent) {
      case 'gestao':
        return <GestaoExpeditionPage />;

      case 'relatorios':
        // Se há um relatório selecionado, mostrar o componente correspondente
        if (relatorioSelecionado === 'bipes') {
          return (
            <RelatoriosSection 
              onVoltar={handleVoltarRelatorios}
            />
          );
        }
        if (relatorioSelecionado === 'expedicao') {
          return (
            <RelatorioExpedicao 
              onVoltar={handleVoltarRelatorios}
            />
          );
        }
        // Caso contrário, mostrar a página inicial
        return <RelatoriosHome onSelectRelatorio={handleSelectRelatorio} />;

      case 'configuracoes':
        // Se há uma configuração selecionada, mostrar o componente correspondente
        if (configuracaoSelecionada === 'usuarios') {
          return <UsuariosSection onVoltar={handleVoltarConfiguracoes} />;
        }
        // Caso contrário, mostrar a página inicial de configurações
        return <ConfiguracoesHome onSelectConfiguracao={handleSelectConfiguracao} />;

      default:
        return (
          <div className="admin-placeholder">
            <div className="placeholder-icon">❓</div>
            <h2>Página não encontrada</h2>
            <p>Selecione uma opção no menu lateral</p>
          </div>
        );
    }
  };

  return (
    <div className={`admin-page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <AdminSidebar
        activeMenu={activeMenu}
        onMenuChange={handleMenuChange}
        collapsed={sidebarCollapsed}
        onToggleCollapse={handleToggleSidebar}
      />
      <div className="admin-main-content" style={isPending ? { opacity: 0.85, pointerEvents: 'none', transition: 'opacity 0.15s' } : undefined}>
        <Suspense fallback={<SuspenseFallback />}>
          {renderContent()}
        </Suspense>
      </div>
    </div>
  );
};

export default AdminPage;
